import { useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  API,
  formatDate,
  formatApiErrorDetail,
} from "@/magazzino/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  Printer,
  Barcode,
  MagnifyingGlass,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import { printDDT, printMultipleDDT } from "@/magazzino/lib/ddt";
import BarcodeScannerDialog from "@/magazzino/components/BarcodeScannerDialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = {
  product_id: "",
  quantity: "",
  note: "",
  reference: "",
  cantiere_id: "",
  lavorazione_id: "",
};

export default function MovementsPage() {
  const isAlluminio = window.location.pathname.startsWith("/gestionale/magazzino-alluminio");
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [sites, setSites] = useState([]);
  const [type, setType] = useState("in");
  const [form, setForm] = useState(emptyForm);

  const [filter, setFilter] = useState({
    type: "all",
    product_id: "all",
    date_from: "",
    date_to: "",
  });

  const [scanOpen, setScanOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productResultsOpen, setProductResultsOpen] =
    useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedMovementIds, setSelectedMovementIds] = useState([]);
  const [works, setWorks] = useState([]);
  const [destinationOpen, setDestinationOpen] = useState(false);
  const [destinationMovement, setDestinationMovement] = useState(null);
  const [destination, setDestination] = useState({
    cantiere_id: "",
    lavorazione_id: "",
  });
  const [savingDestination, setSavingDestination] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnMovement, setReturnMovement] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState("");
  const [returnNote, setReturnNote] = useState("");
  const [savingReturn, setSavingReturn] = useState(false);

  const selectableMovements = useMemo(
    () => movements.filter((movement) => movement.type === "out"),
    [movements]
  );
  const allSelectableSelected = selectableMovements.length > 0
    && selectableMovements.every((movement) => selectedMovementIds.includes(movement.id));

  useEffect(() => {
    const visibleIds = new Set(selectableMovements.map((movement) => movement.id));
    setSelectedMovementIds((current) => current.filter((id) => visibleIds.has(id)));
  }, [selectableMovements]);

  const toggleMovementSelection = (movementId) => {
    setSelectedMovementIds((current) => current.includes(movementId)
      ? current.filter((id) => id !== movementId)
      : [...current, movementId]);
  };

  const toggleAllMovements = () => {
    const visibleIds = selectableMovements.map((movement) => movement.id);
    setSelectedMovementIds((current) => allSelectableSelected
      ? current.filter((id) => !visibleIds.includes(id))
      : [...new Set([...current, ...visibleIds])]);
  };

  const printSelectedMovements = async () => {
    try {
      await printMultipleDDT(selectedMovementIds);
    } catch (error) {
      toast.error(error?.message || "Impossibile generare il DDT multiplo");
    }
  };

  const loadWorks = useCallback(async (cantiereId) => {
    if (!cantiereId) {
      setWorks([]);
      return;
    }
    try {
      const response = await api.get(`/cantieri/${cantiereId}/lavorazioni`);
      setWorks(Array.isArray(response.data) ? response.data : []);
    } catch {
      setWorks([]);
    }
  }, []);

  const load = async () => {
    const params = {};

    if (filter.type !== "all") {
      params.type = filter.type;
    }

    if (filter.product_id !== "all") {
      params.product_id = filter.product_id;
    }

    if (filter.date_from) {
      params.date_from = filter.date_from;
    }

    if (filter.date_to) {
      params.date_to = filter.date_to;
    }

    try {
      const [productsResponse, movementsResponse] =
        await Promise.all([
          api.get("/prodotti"),
          api.get("/movimenti", { params }),
        ]);

      setProducts(productsResponse.data);
      setMovements(movementsResponse.data);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante il caricamento dei dati"
      );
    }
  };

  /* Unisce i cantieri CantierePro con le destinazioni storiche del Magazzino. */
  const loadSites = useCallback(async () => {
    try {
      if (isAlluminio) {
        const response = await api.get("/reports/lavori", { params: { archived: false } });
        const lavori = Array.isArray(response.data?.rows) ? response.data.rows : [];
        setSites(lavori.map((lavoro) => ({
          id: "", nome: lavoro.reference, origine: "magazzino", stato: "",
        })));
        return;
      }
      const [cantieriResponse, movimentiResponse] = await Promise.all([
        api.get("/cantieri"),
        api.get("/movimenti", { params: { type: "out" } }),
      ]);
      const byName = new Map();
      const cantieri = Array.isArray(cantieriResponse.data)
        ? cantieriResponse.data
        : [];

      const cantieriCompletati = new Set(
        cantieri
          .filter((cantiere) => {
            const stato = String(cantiere.stato || "")
              .trim()
              .toLocaleLowerCase("it");

            return stato === "completato" || stato === "completed";
          })
          .map((cantiere) =>
            String(cantiere.nome || "")
              .trim()
              .toLocaleLowerCase("it")
          )
          .filter(Boolean)
      );

      cantieri.forEach((cantiere) => {
        const nome = String(cantiere.nome || "").trim();
        const key = nome.toLocaleLowerCase("it");

        if (nome && !cantieriCompletati.has(key)) {
          byName.set(key, {
            ...cantiere,
            origine: "cantierepro",
          });
        }
      });
      const movimenti = Array.isArray(movimentiResponse.data)
        ? movimentiResponse.data
        : [];
      movimenti.forEach((movement) => {
        const nome = String(movement.reference || "").trim();
        const key = nome.toLocaleLowerCase("it");
        if (nome && !cantieriCompletati.has(key) && !byName.has(key)) {
          byName.set(key, { id: "", nome, origine: "magazzino", stato: "" });
        }
      });
      const elenco = [...byName.values()].sort((a, b) =>
        a.nome.localeCompare(b.nome, "it", {
          sensitivity: "base",
        })
      );
      setSites(elenco);
    } catch {
      setSites([]);
    }
  }, [isAlluminio]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const onScan = useCallback(async (code) => {
    setScanOpen(false);

    try {
      const { data } = await api.get(
        "/prodotti/by-barcode",
        { params: { code: String(code || "").trim() } }
      );

      setForm((current) => ({
        ...current,
        product_id: data.id,
        quantity: current.quantity || "1",
      }));

      setProductSearch(`${data.sku} · ${data.name}`);
      setProductResultsOpen(false);

      toast.success(`Prodotto rilevato: ${data.name}`);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Codice non trovato"
      );
    }
  }, []);

  const submit = async (event) => {
    event.preventDefault();

    if (!form.product_id || !form.quantity) {
      toast.error("Prodotto e quantità obbligatori");
      return;
    }

    if (Number(form.quantity) <= 0) {
      toast.error("Inserisci una quantità valida");
      return;
    }

    if (type === "out" && !form.reference.trim()) {
      toast.error(isAlluminio ? "Inserisci la destinazione o il riferimento" : "Inserisci il cantiere di riferimento");
      return;
    }

    try {
      await api.post("/movimenti", {
        product_id: form.product_id,
        type,
        quantity: Number(form.quantity),
        note: form.note,
        reference: form.reference.trim(),
        cantiere_id: form.cantiere_id || null,
        lavorazione_id: form.lavorazione_id || null,
      });

      toast.success(
        type === "in"
          ? "Carico registrato"
          : "Scarico registrato"
      );

      setForm(emptyForm);
      setProductSearch("");
      setProductResultsOpen(false);

      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail)
      );
    }
  };

  const removeMovement = async (movement) => {
    const product = products.find(
      (item) => item.id === movement.product_id
    );

    const label =
      movement.type === "in" ? "carico" : "scarico";

    const stockMessage =
      movement.type === "in"
        ? `La giacenza verrà diminuita di ${movement.quantity}.`
        : `La giacenza verrà aumentata di ${movement.quantity}.`;

    const confirmed = window.confirm(
      `Eliminare questo ${label} di ${movement.quantity} ` +
        `${product?.unit || ""} per "${product?.name || "il prodotto"}"?\n\n` +
        `${stockMessage}\n\nL'operazione non può essere annullata.`
    );

    if (!confirmed) return;

    setDeletingId(movement.id);

    try {
      await api.delete(`/movimenti/${movement.id}`);
      toast.success("Movimento eliminato e giacenza aggiornata");
      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante l'eliminazione del movimento"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const remainingReturnQuantity = (movement) => Math.max(
    Number(movement?.quantity || 0) - Number(movement?.returned_quantity || 0),
    0
  );

  const openReturn = (movement) => {
    const remaining = remainingReturnQuantity(movement);
    setReturnMovement(movement);
    setReturnQuantity(String(remaining));
    setReturnNote("");
    setReturnOpen(true);
  };

  const saveReturn = async () => {
    const quantity = Number(returnQuantity);
    const remaining = remainingReturnQuantity(returnMovement);
    if (!(quantity > 0)) {
      toast.error("Inserisci una quantità valida");
      return;
    }
    if (quantity > remaining) {
      toast.error(`Puoi restituire al massimo ${remaining}`);
      return;
    }
    setSavingReturn(true);
    try {
      await api.post(`/movimenti/${returnMovement.id}/reso`, {
        quantita: quantity,
        note: returnNote.trim(),
      });
      toast.success("Reso registrato: giacenza e materiale del cantiere aggiornati");
      setReturnOpen(false);
      setReturnMovement(null);
      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante la registrazione del reso"
      );
    } finally {
      setSavingReturn(false);
    }
  };

  const openDestination = async (movement) => {
    const cantiereId = movement.cantierepro_cantiere_id || "";
    setDestinationMovement(movement);
    setDestination({
      cantiere_id: cantiereId,
      lavorazione_id: movement.lavorazione_id || "",
    });
    setDestinationOpen(true);
    await loadWorks(cantiereId);
  };

  const saveDestination = async () => {
    if (!destinationMovement || !destination.cantiere_id) {
      toast.error("Seleziona il cantiere di destinazione");
      return;
    }
    setSavingDestination(true);
    try {
      await api.put(`/movimenti/${destinationMovement.id}/destinazione`, {
        cantiere_id: destination.cantiere_id,
        lavorazione_id: destination.lavorazione_id || null,
      });
      toast.success("Destinazione dello scarico aggiornata");
      setDestinationOpen(false);
      setDestinationMovement(null);
      await Promise.all([load(), loadSites()]);
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          "Errore durante la modifica della destinazione"
      );
    } finally {
      setSavingDestination(false);
    }
  };

  const productName = (id) => {
    const product = products.find(
      (item) => item.id === id
    );

    return product
      ? `${product.sku} · ${product.name}`
      : "—";
  };

  const filteredProducts = useMemo(() => {
    const search = productSearch
      .trim()
      .toLowerCase();

    return products
      .filter((product) => {
        if (!search) return true;

        return [
          product.sku,
          product.name,
          product.description,
          product.barcode,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(search)
        );
      })
      .slice(0, 15);
  }, [products, productSearch]);

  const productPhotoUrl = (product) => product?.photo_path
    ? `${API}/gestionale/magazzino/prodotti/${product.id}/foto?magazzino_tipo=${isAlluminio ? "alluminio" : "standard"}&v=${encodeURIComponent(product.photo_updated_at || "1")}`
    : "";

  const selectedProduct = products.find(
    (product) => product.id === form.product_id
  );

  const selectProduct = (product) => {
    setForm((current) => ({
      ...current,
      product_id: product.id,
    }));

    setProductSearch(
      `${product.sku} · ${product.name}`
    );

    setProductResultsOpen(false);
  };

  const changeMovementType = (newType) => {
    setType(newType);

    setForm((current) => ({
      ...current,
      reference: "",
      cantiere_id: "",
      lavorazione_id: "",
    }));
    setWorks([]);
  };

  return (
    <div className="grid min-w-0 grid-cols-1 gap-6 pb-[max(0.5rem,env(safe-area-inset-bottom))] xl:h-full xl:min-h-0 xl:grid-cols-3 xl:pb-0">
      <Card
        className="h-fit min-w-0 rounded-2xl border-slate-200 p-4 shadow-sm sm:p-5 xl:col-span-1"
        data-testid="movement-form"
      >
        <div className="mb-1 text-lg font-bold tracking-tight text-slate-950">
          Registra movimento
        </div>

        <div className="mb-4 text-xs text-slate-500">
          {isAlluminio ? "Carico e scarico del Magazzino Alluminio" : <>Carico merce in entrata o scarico verso un cantiere</>}
        </div>

        <Tabs
          value={type}
          onValueChange={changeMovementType}
          className="mb-4"
        >
          <TabsList className="grid h-10 grid-cols-2 bg-slate-100">
            <TabsTrigger
              value="in"
              data-testid="tab-carico"
              className="gap-1.5 data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <ArrowDown size={14} weight="bold" />
              Carico
            </TabsTrigger>

            <TabsTrigger
              value="out"
              data-testid="tab-scarico"
              className="gap-1.5 data-[state=active]:bg-orange-600 data-[state=active]:text-white"
            >
              <ArrowUp size={14} weight="bold" />
              Scarico
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <form
          onSubmit={submit}
          className="space-y-4"
        >
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Prodotto *
              </Label>

              <button
                type="button"
                onClick={() => setScanOpen(true)}
                data-testid="scan-barcode-btn"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              >
                <Barcode size={14} weight="bold" />
                Scansiona
              </button>
            </div>

            <div className="relative mt-1">
              <MagnifyingGlass
                size={17}
                weight="bold"
                className="pointer-events-none absolute left-3 top-[22px] z-10 -translate-y-1/2 text-slate-400"
              />

              <Input
                value={productSearch}
                onChange={(event) => {
                  setProductSearch(
                    event.target.value
                  );

                  setForm((current) => ({
                    ...current,
                    product_id: "",
                  }));

                  setProductResultsOpen(true);
                }}
                onFocus={() =>
                  setProductResultsOpen(true)
                }
                placeholder="Scrivi nome, descrizione, SKU o barcode..."
                className="h-11 bg-white pl-10 pr-10"
                autoComplete="off"
                data-testid="movement-product-search"
              />

              {productSearch && (
                <button
                  type="button"
                  onClick={() => {
                    setProductSearch("");

                    setForm((current) => ({
                      ...current,
                      product_id: "",
                    }));

                    setProductResultsOpen(true);
                  }}
                  className="absolute right-3 top-[22px] -translate-y-1/2 text-lg text-slate-400 hover:text-slate-700"
                  aria-label="Cancella prodotto"
                >
                  ×
                </button>
              )}

              {productResultsOpen && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[50dvh] touch-pan-y overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white p-1 shadow-xl sm:max-h-80">
                  {filteredProducts.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">
                      Nessun prodotto trovato.
                    </div>
                  ) : (
                    filteredProducts.map(
                      (product) => {
                        const lowStock =
                          product.quantity <=
                          product.min_stock;

                        return (
                          <button
                            key={product.id}
                            type="button"
                            onMouseDown={(event) =>
                              event.preventDefault()
                            }
                            onClick={() =>
                              selectProduct(product)
                            }
                            className="flex w-full items-start justify-between gap-4 rounded-lg px-3 py-3 text-left hover:bg-slate-50"
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              {product.photo_path ? (
                                <img
                                  src={productPhotoUrl(product)}
                                  alt={product.name}
                                  className="h-12 w-12 shrink-0 rounded-md border border-slate-200 object-cover"
                                />
                              ) : (
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-[10px] text-slate-400">
                                  No foto
                                </div>
                              )}

                              <div className="min-w-0">
                                <div className="font-semibold text-slate-900">
                                  {product.name}
                                </div>

                              <div className="mt-1 text-xs text-slate-500">
                                SKU: {product.sku}
                                {product.barcode
                                  ? ` · Barcode: ${product.barcode}`
                                  : ""}
                              </div>

                                {product.description && (
                                  <div className="mt-1 line-clamp-2 text-xs text-slate-500">
                                    {product.description}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="shrink-0 text-right">
                              <div
                                className={`text-sm font-semibold ${
                                  lowStock
                                    ? "text-red-600"
                                    : "text-emerald-600"
                                }`}
                              >
                                {product.quantity}{" "}
                                {product.unit}
                              </div>

                              <div className="mt-1 text-xs text-slate-400">
                                Disponibile
                              </div>
                            </div>
                          </button>
                        );
                      }
                    )
                  )}
                </div>
              )}
            </div>

            {selectedProduct && (
              <div className="mt-2 flex flex-col items-stretch gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  {selectedProduct.photo_path ? (
                    <img
                      src={productPhotoUrl(selectedProduct)}
                      alt={selectedProduct.name}
                      className="h-16 w-16 shrink-0 rounded-lg border border-emerald-200 bg-white object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-white text-[10px] text-slate-400">
                      No foto
                    </div>
                  )}

                  <div className="min-w-0">
                    <div className="text-xs text-emerald-700">
                      Prodotto selezionato
                    </div>
                    <div className="truncate font-semibold text-emerald-900">
                      {selectedProduct.name}
                    </div>
                    <div className="text-xs text-emerald-700">
                      SKU: {selectedProduct.sku}
                    </div>
                  </div>
                </div>

                <span className="shrink-0 text-xs font-medium text-emerald-700 sm:text-right">
                  {selectedProduct.quantity}{" "}
                  {selectedProduct.unit} disponibili
                </span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">
              Quantità *
            </Label>

            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={form.quantity}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  quantity: event.target.value,
                }))
              }
              data-testid="movement-quantity-input"
              className="mt-1"
            />
          </div>

          {type === "in" ? (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                Riferimento (DDT / N° ordine)
              </Label>

              <Input
                value={form.reference}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reference: event.target.value,
                    cantiere_id: "",
                  }))
                }
                placeholder="Inserisci DDT o numero ordine"
                data-testid="movement-reference-input"
                className="mt-1"
              />
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">
                {isAlluminio ? "Destinazione / riferimento *" : "Cantiere di riferimento *"}
              </Label>

              <SearchableCombobox
                value={form.reference}
                onValueChange={(reference) => {
                  const selected = sites.find(
                    (site) => site.nome.localeCompare(reference.trim(), "it", { sensitivity: "base" }) === 0
                  );
                  setForm((current) => ({
                    ...current,
                    reference,
                    cantiere_id: selected?.id || "",
                    lavorazione_id: "",
                  }));
                  loadWorks(selected?.id || "");
                }}
                options={sites
                  .filter((site) => {
                    const stato = String(site.stato || "")
                      .trim()
                      .toLocaleLowerCase("it");
                    return stato !== "completato" && stato !== "completed";
                  })
                  .map((site) => ({
                  value: site.nome,
                  label: isAlluminio ? site.nome : `${site.nome} — ${site.origine === "cantierepro" ? "CantierePro" : "Magazzino"}`,
                  keywords: site.nome,
                }))}
                placeholder={isAlluminio ? "Scrivi o seleziona una destinazione già usata" : "Scrivi o seleziona un cantiere già usato"}
                searchPlaceholder={isAlluminio ? "Cerca destinazione..." : "Cerca cantiere..."}
                emptyText={isAlluminio ? "Nessuna destinazione trovata" : "Nessun cantiere trovato"}
                allowCustom
                className="mt-1"
                testId="movement-reference-input"
              />

              <div className="mt-1 text-[11px] text-slate-500">
                {isAlluminio ? "Sono mostrate esclusivamente le destinazioni già usate nel Magazzino Alluminio." : "Sono disponibili i cantieri CantierePro attivi e le destinazioni già usate nel Magazzino."}
              </div>

              {form.cantiere_id && (
                <div className="mt-4">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">
                    Lavorazione (facoltativa)
                  </Label>
                  <Select
                    value={form.lavorazione_id || "nessuna"}
                    onValueChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        lavorazione_id: value === "nessuna" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="Nessuna lavorazione" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="nessuna">Nessuna lavorazione</SelectItem>
                      {works.map((work) => (
                        <SelectItem key={work.id} value={work.id}>
                          {work.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">
              Note
            </Label>

            <Textarea
              rows={3}
              value={form.note}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  note: event.target.value,
                }))
              }
              placeholder="Inserisci eventuali annotazioni..."
              data-testid="movement-note-input"
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            data-testid="movement-submit-btn"
            className={`h-11 w-full text-white ${
              type === "in"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-orange-600 hover:bg-orange-700"
            }`}
          >
            Registra{" "}
            {type === "in" ? "Carico" : "Scarico"}
          </Button>
        </form>

        <BarcodeScannerDialog
          open={scanOpen}
          onOpenChange={setScanOpen}
          onDetected={onScan}
        />
      </Card>

      <Card
        className="overflow-hidden rounded-2xl border-slate-200 shadow-sm xl:col-span-2 xl:flex xl:min-h-0 xl:flex-col"
        data-testid="movements-history"
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-lg font-bold tracking-tight">
              Storico movimenti
            </div>

            <div className="text-xs text-slate-500">
              Filtra per tipo, prodotto e periodo
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={selectedMovementIds.length === 0}
            onClick={printSelectedMovements}
            className="shrink-0"
          >
            <Printer size={16} weight="bold" className="mr-2" />
            Stampa DDT selezionati ({selectedMovementIds.length})
          </Button>

          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 md:flex">
            <Select
              value={filter.type}
              onValueChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  type: value,
                }))
              }
            >
              <SelectTrigger
                className="h-9 w-full bg-white md:w-[130px]"
                data-testid="filter-type"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent className="bg-white">
                <SelectItem value="all">
                  Tutti i tipi
                </SelectItem>
                <SelectItem value="in">
                  Solo Carico
                </SelectItem>
                <SelectItem value="out">
                  Solo Scarico
                </SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.product_id}
              onValueChange={(value) =>
                setFilter((current) => ({
                  ...current,
                  product_id: value,
                }))
              }
            >
              <SelectTrigger
                className="h-9 w-full bg-white md:w-[180px]"
                data-testid="filter-product"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent className="max-h-72 bg-white">
                <SelectItem value="all">
                  Tutti i prodotti
                </SelectItem>

                {products.map((product) => (
                  <SelectItem
                    key={product.id}
                    value={product.id}
                  >
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filter.date_from}
              onChange={(event) =>
                setFilter((current) => ({
                  ...current,
                  date_from: event.target.value,
                }))
              }
              className="h-9 w-full md:w-[150px]"
              data-testid="filter-date-from"
            />

            <Input
              type="date"
              value={filter.date_to}
              onChange={(event) =>
                setFilter((current) => ({
                  ...current,
                  date_to: event.target.value,
                }))
              }
              className="h-9 w-full md:w-[150px]"
              data-testid="filter-date-to"
            />
          </div>
        </div>

        <div className="overflow-x-auto xl:flex-1 xl:min-h-0 xl:overflow-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 font-medium">
                  Data
                </th>
                <th className="px-5 py-3 font-medium">
                  Tipo
                </th>
                <th className="px-5 py-3 font-medium">
                  Prodotto
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  Qtà
                </th>
                <th className="px-5 py-3 font-medium">
                  {isAlluminio ? "DDT / Destinazione" : "DDT / Cantiere"}
                </th>
                <th className="px-5 py-3 font-medium">
                  Operatore
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  DDT
                </th>
                <th className="px-5 py-3 text-right font-medium">
                  <span className="inline-flex items-center justify-end gap-2">
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      onChange={toggleAllMovements}
                      disabled={selectableMovements.length === 0}
                      aria-label="Seleziona tutti gli scarichi visibili"
                      className="h-4 w-4 accent-orange-600"
                    />
                    Azioni
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {movements.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-slate-500"
                  >
                    Nessun movimento nel periodo
                    selezionato.
                  </td>
                </tr>
              )}

              {movements.map((movement) => (
                <tr
                  key={movement.id}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`movement-row-${movement.id}`}
                >
                  <td className="px-5 py-2.5 text-slate-600 tabular">
                    {formatDate(
                      movement.created_at
                    )}
                  </td>

                  <td className="px-5 py-2.5">
                    <span
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
                        movement.type === "in"
                          ? "bg-green-50 text-green-700"
                          : "bg-orange-50 text-orange-700"
                      }`}
                    >
                      {movement.type === "in" ? (
                        <ArrowDown
                          size={12}
                          weight="bold"
                        />
                      ) : (
                        <ArrowUp
                          size={12}
                          weight="bold"
                        />
                      )}

                      {movement.movement_kind === "return"
                        ? "Reso"
                        : movement.type === "in"
                        ? "Carico"
                        : "Scarico"}
                    </span>
                  </td>

                  <td className="px-5 py-2.5">
                    {productName(
                      movement.product_id
                    )}
                  </td>

                  <td className="px-5 py-2.5 text-right font-medium tabular">
                    <span>{movement.quantity}</span>
                    {movement.type === "out" && Number(movement.returned_quantity || 0) > 0 && (
                      <span className="block whitespace-nowrap text-[11px] font-normal text-emerald-600">
                        Resi {movement.returned_quantity} · nel cantiere {remainingReturnQuantity(movement)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-slate-600">
                    {movement.reference || "—"}
                  </td>

                  <td className="max-w-[180px] truncate px-5 py-2.5 text-slate-600">
                    {movement.user_name || "—"}
                  </td>

                  <td className="px-5 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => printDDT(movement.id)}
                      data-testid={`ddt-print-${movement.id}`}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white"
                      title={
                        movement.type === "in"
                          ? "Stampa DDT di carico"
                          : "Stampa DDT di scarico"
                      }
                    >
                      <Printer size={14} weight="bold" />
                      DDT
                    </button>
                  </td>

                  <td className="px-5 py-2.5 text-right">
                    {movement.type === "out" && (
                      <input
                        type="checkbox"
                        checked={selectedMovementIds.includes(movement.id)}
                        onChange={() => toggleMovementSelection(movement.id)}
                        aria-label={`Seleziona ${productName(movement.product_id)}`}
                        className="mr-2 h-4 w-4 align-middle accent-orange-600"
                      />
                    )}
                    {!isAlluminio && movement.type === "out" && (
                      <button
                        type="button"
                        onClick={() => openDestination(movement)}
                        className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-orange-600 transition hover:bg-orange-50"
                        title="Modifica cantiere e lavorazione"
                        aria-label="Modifica destinazione scarico"
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                    )}
                    {!isAlluminio && movement.type === "out" && movement.cantierepro_materiale_id && remainingReturnQuantity(movement) > 0 && (
                      <button
                        type="button"
                        onClick={() => openReturn(movement)}
                        className="mr-1 inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50"
                        title="Restituisci al magazzino"
                        aria-label="Registra reso al magazzino"
                      >
                        <ArrowDown size={15} weight="bold" />
                        Reso
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeMovement(movement)}
                      disabled={deletingId === movement.id}
                      data-testid={`delete-movement-${movement.id}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title="Elimina movimento"
                      aria-label="Elimina movimento"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog
        open={returnOpen}
        onOpenChange={(open) => {
          setReturnOpen(open);
          if (!open) setReturnMovement(null);
        }}
      >
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Reso al magazzino</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="font-semibold text-emerald-900">
                {returnMovement ? productName(returnMovement.product_id) : ""}
              </div>
              <div className="mt-1 text-emerald-700">
                Cantiere: {returnMovement?.reference || "—"} · Restituibile: {remainingReturnQuantity(returnMovement)}
              </div>
            </div>
            <div>
              <Label>Quantità da restituire *</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={remainingReturnQuantity(returnMovement)}
                value={returnQuantity}
                onChange={(event) => setReturnQuantity(event.target.value)}
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                value={returnNote}
                onChange={(event) => setReturnNote(event.target.value)}
                placeholder="Motivo o annotazioni sul reso"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReturnOpen(false)}>
              Annulla
            </Button>
            <Button type="button" onClick={saveReturn} disabled={savingReturn} className="bg-emerald-600 text-white hover:bg-emerald-700">
              {savingReturn ? "Registrazione..." : "Conferma reso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={destinationOpen}
        onOpenChange={(open) => {
          setDestinationOpen(open);
          if (!open) {
            setDestinationMovement(null);
            setWorks([]);
          }
        }}
      >
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Modifica destinazione scarico</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Cantiere di destinazione *</Label>
              <SearchableCombobox
                value={destination.cantiere_id}
                onValueChange={(value) => {
                  setDestination({ cantiere_id: value, lavorazione_id: "" });
                  loadWorks(value);
                }}
                options={sites.filter(site=>site.id).map(site=>({value:site.id,label:site.nome}))}
                placeholder="Seleziona cantiere" searchPlaceholder="Scrivi il cantiere..." className="mt-1" />
            </div>

            <div>
              <Label>Lavorazione (facoltativa)</Label>
              <Select
                value={destination.lavorazione_id || "nessuna"}
                onValueChange={(value) =>
                  setDestination((current) => ({
                    ...current,
                    lavorazione_id: value === "nessuna" ? "" : value,
                  }))
                }
                disabled={!destination.cantiere_id}
              >
                <SelectTrigger className="mt-1 bg-white">
                  <SelectValue placeholder="Nessuna lavorazione" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="nessuna">Nessuna lavorazione</SelectItem>
                  {works.map((work) => (
                    <SelectItem key={work.id} value={work.id}>
                      {work.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDestinationOpen(false)}>
              Annulla
            </Button>
            <Button
              type="button"
              onClick={saveDestination}
              disabled={savingDestination || !destination.cantiere_id}
              className="bg-orange-600 text-white hover:bg-orange-700"
            >
              {savingDestination ? "Salvataggio..." : "Salva destinazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
